import { useEffect } from "react";
import { mixpanel, redactUrlForTelemetry } from "../utils";
import { JOURNEY_EVENTS, MIXPANEL_EVENTS } from "../constants";
import { getGtmRouteKey, trackGtmJourneyEvent } from "../utils/gtmDataLayer";

// multi-variable destructuring breaks dotenv-webpack
const QUIQ_CHAT_URL = process.env.QUIQ_CHAT_URL;
const QUIQ_CHAT_HOST = process.env.QUIQ_CHAT_HOST;
const QUIQ_CHAT_CONTACT_POINT = process.env.QUIQ_CHAT_CONTACT_POINT;
const QUIQ_CHAT_SECONDS_BEFORE_RENDER =
  process.env.QUIQ_CHAT_SECONDS_BEFORE_RENDER;

const config = {
  pageConfigurationId: QUIQ_CHAT_CONTACT_POINT,
  host: QUIQ_CHAT_HOST,
  autoPopTime: (QUIQ_CHAT_SECONDS_BEFORE_RENDER || 0) * 1000,
};

const scriptId = "quiq-chat-load";
const contentScriptId = "quiq-chat-run";
const DEFAULT_CHAT_DEFER_MS = 1500;

const trackOnMixPanel = () => {
  mixpanel.track(MIXPANEL_EVENTS.openChatWidget, {
    linkName: "quiq-chat-button",
    location: redactUrlForTelemetry(document.location.href),
  });
  trackGtmJourneyEvent(JOURNEY_EVENTS.chatOpened, {
    route_key: getGtmRouteKey(),
  });
};

export default function useQuiqChat(context = {}, verified = true, options = {}) {
  useEffect(() => {
    // abort if unverified or if not in a browser
    if (!verified || typeof window === "undefined") return;
    if (!QUIQ_CHAT_URL) return;

    const trackQuiqButtonClick = () => {
      const quiqButton = document.querySelector(
        ".quiq-action-toggle.quiq-togglechatbutton-button",
      );

      if (quiqButton) {
        quiqButton.addEventListener("click", trackOnMixPanel, true);
      }
    };

    const deferMs = Number.isFinite(options.deferMs)
      ? options.deferMs
      : DEFAULT_CHAT_DEFER_MS;
    let cancelled = false;
    let timeoutId;
    let idleCallbackId;
    let quiqChat;
    let observer;

    const startChat = () => {
      if (cancelled || document.getElementById(scriptId)) return;

      const script = document.createElement("script");

      script.onload = () => {
        if (cancelled || typeof Quiq === "undefined") return;

        quiqChat = new Quiq(config);
        window.quiqChat = quiqChat;
        quiqChat.setContext(context, true);

        observer = new MutationObserver(() => {
          if (
            document.querySelector(
              ".quiq-action-toggle.quiq-togglechatbutton-button",
            )
          ) {
            trackQuiqButtonClick();
            observer.disconnect();
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
        timeoutId = window.setTimeout(trackQuiqButtonClick, 1000);
      };

      script.setAttribute("id", scriptId);
      script.setAttribute("async", true);
      script.setAttribute("charset", "UTF-8");
      script.setAttribute("data-cfasync", "false");
      script.setAttribute("type", "application/javascript");
      script.setAttribute("src", QUIQ_CHAT_URL);

      document.body.appendChild(script);
    };

    if ("requestIdleCallback" in window) {
      idleCallbackId = window.requestIdleCallback(startChat, { timeout: deferMs });
    } else {
      timeoutId = window.setTimeout(startChat, deferMs);
    }

    // return cleanup callback
    return () => {
      cancelled = true;
      if (idleCallbackId && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId) window.clearTimeout(timeoutId);
      if (observer) observer.disconnect();

      // Remove event listener from Quiq button if it exists
      const quiqButton = document.querySelector(
        ".quiq-action-toggle.quiq-togglechatbutton-button",
      );
      if (quiqButton) {
        quiqButton.removeEventListener("click", trackOnMixPanel, true);
      }

      const activeQuiqChat = quiqChat || window.quiqChat;

      activeQuiqChat?.clearUserSession?.();

      if (activeQuiqChat?.destroy instanceof Function) {
        activeQuiqChat.destroy();
      } else if (activeQuiqChat?.unload instanceof Function) {
        activeQuiqChat.unload();
      }

      if (window.quiqChat === activeQuiqChat) {
        delete window.quiqChat;
      }

      document.getElementById(scriptId)?.remove();
      document.getElementById(contentScriptId)?.remove();
    };
  }, [options.deferMs, verified]);
}
